// =============================================================
// main.ts — MoleBang UI. 기존 index.html 로직을 이식하되,
//   지갑 연결만 SDK(Reown/injected 자동 선택)로 교체.
// =============================================================

import { BrowserProvider, Contract, parseEther } from 'ethers';
import './style.css';
import { I18N, EXCHANGES_BY_LANG } from './i18n';
import { createWalletAdapter, type WalletAdapter } from './sdk/wallet';
import { initDebug, dlog } from './debug';
import { CHAIN, CLAIM_CONTRACT, MOL_TOKEN, MOL_SYMBOL, MOL_DECIMALS, API_BASE, TG_CHANNEL, TG_GROUP, TG_BOT_USERNAME , DONATE_ADDRESS} from './sdk/config';

import {
  detectInApp,
  isInAppBrowser,
  escapeToExternalBrowser,
  manualEscapeHint,
  isIOS,
  isAndroid,
} from './inapp';

// ---------- 상수 ----------
const BACKEND = API_BASE; // 통합 서버: 같은 origin
const BSC_CHAIN_ID = CHAIN.idHex;
// MOLSealClaim ABI — 인장 보상 전용 컨트랙트.
//  claimSeal(sealType, deadline, v, r, s)
//    sealType: 0 = X, 1 = Telegram
const SEAL_CLAIM_ABI = [
  'function claimSeal(uint8 sealType, uint256 deadline, uint8 v, bytes32 r, bytes32 s)',
  'function claimStatus(address wallet) view returns (bool,bool)',
];

const CLAIM_ABI = [
  'function claim(bytes32[] txids, uint256 deadline, uint8 v, bytes32 r, bytes32 s) external',
  'function nonces(address) view returns (uint256)',
];
const MAX_TXIDS = 10;

// ---------- 상태 ----------
let lang = 'en';
let account: string | null = null;
let validTxids: string[] = [];
let seals: Record<string, boolean> = { deposit: false, x: false, telegram: false };
const wallet: WalletAdapter = createWalletAdapter();

// ---------- 유틸 ----------
const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el;
};
function short(a: string): string {
  return a.slice(0, 6) + '…' + a.slice(-4);
}
function t(): any {
  return I18N[lang];
}
function post(p: string, b: unknown): Promise<any> {
  return fetch(BACKEND + p, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(b),
  }).then((r) => r.json());
}
function getJSON(p: string): Promise<any> {
  return fetch(BACKEND + p).then((r) => r.json());
}

// ---------- i18n ----------
function applyI18n(): void {
  const d = I18N[lang];
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n')!;
    if (d[key] != null && typeof d[key] !== 'function') el.textContent = d[key];
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach((el) => {
    const key = el.getAttribute('data-i18n-html')!;
    if (d[key] != null) el.innerHTML = d[key];
  });
  renderExchanges();
  renderSeals();
  const langBtns = document.querySelectorAll<HTMLElement>('#lang button');
  langBtns.forEach((b) => b.classList.toggle('active', b.getAttribute('data-lang') === lang));
}

const EXCHANGE_KEY: Record<string, string> = {
  Binance: 'binance',
  Coinbase: 'coinbase',
  Bithumb: 'bithumb',
  bitFlyer: 'bitflyer',
};
function currentExchanges(): string[] {
  return EXCHANGES_BY_LANG[lang] || EXCHANGES_BY_LANG.en;
}
function renderExchanges(): void {
  const list = currentExchanges();
  const chipsEl = document.getElementById('exchChips');
  if (chipsEl) chipsEl.innerHTML = list.map((x) => `<span class="chip">${x}</span>`).join('');
  const hintEl = document.getElementById('scanHintTxt');
  const joined = list.join(' / ');
  if (hintEl && typeof I18N[lang].scanHint === 'function') hintEl.textContent = I18N[lang].scanHint(joined);

  const wrap = document.getElementById('exchLinks');
  if (wrap && window.MoleLinks) {
    wrap.innerHTML = list
      .map((x) => {
        const key = EXCHANGE_KEY[x] || x.toLowerCase();
        return `<a class="link-chip" href="#" data-exch="${key}"><span>${x}</span></a>`;
      })
      .join('');
    const chips = wrap.querySelectorAll<HTMLElement>('[data-exch]');
    chips.forEach((chip) => {
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        const key = chip.getAttribute('data-exch')!;
        if (window.MoleLinks) window.MoleLinks.openExchange(key);
      });
    });
  }
  // 제휴/커뮤니티 링크
  if (window.MoleLinks) {
    const dg = document.getElementById('linkDigmol') as HTMLAnchorElement | null;
    const dx = document.getElementById('linkDex') as HTMLAnchorElement | null;
    const dl = document.getElementById('dexLink') as HTMLAnchorElement | null;
    if (dg) dg.href = window.MoleLinks.partnerUrl('digmol');
    if (dx) dx.href = window.MoleLinks.partnerUrl('dexignation');
    if (dl) {
      dl.href = window.MoleLinks.partnerUrl('dexignation');
      dl.target = '_blank';
      dl.rel = 'noopener';
    }
  }
  const tc = document.getElementById('linkTgChannel') as HTMLAnchorElement | null;
  const tgp = document.getElementById('linkTgGroup') as HTMLAnchorElement | null;
  if (tc) tc.href = TG_CHANNEL;
  if (tgp) tgp.href = TG_GROUP;
}

// ---------- 인앱 브라우저 ----------
function setupInApp(): void {
  if (!isInAppBrowser()) return; // PC·일반 모바일 브라우저는 그대로
  const d = I18N[lang];
  // 자동 탈출 시도 (카톡/라인/안드로이드 대부분 성공)
  const auto = escapeToExternalBrowser();

  const bar = document.getElementById('inappBar');
  if (!bar) return;
  const msgEl = document.getElementById('inappMsg');
  if (msgEl) msgEl.textContent = d.inappMsg;
  const btn = document.getElementById('inappBtn');
  if (btn) {
    btn.textContent = d.inappBtn;
    btn.onclick = () => {
      const ok = escapeToExternalBrowser();
      if (ok === false) alert(manualEscapeHint());
    };
  }
  bar.style.display = 'block';
  if (auto === false && isIOS() && msgEl) msgEl.textContent = manualEscapeHint();
}

// ---------- seals ----------
function renderSeals(): void {
  const d = t();
  let count = 0;
  ['deposit', 'x', 'telegram'].forEach((s) => {
    const el = document.getElementById('seal-' + s);
    if (!el) return;
    const on = !!seals[s];
    if (on) count++;
    el.classList.toggle('done', on);
    const st = el.querySelector('.st');
    if (st) st.textContent = on ? d.stDone : d.stLocked;
    const btn = el.querySelector('.seal-btn') as HTMLButtonElement | null;
    if (btn) {
      if (s === 'deposit') {
        // Deposit 은 지갑당 10건까지 → SEALED 여도 10건 미만이면 계속 추가 등록 가능.
        const cnt = Number((seals as any).depositCount || 0);
        const full = cnt >= 10;
        btn.disabled = full;
        // SEALED 라도 아직 등록 가능하면 활성 스타일 유지 ('can-add' 클래스로 .done 회색 오버라이드)
        btn.classList.toggle('can-add', on && !full);
        if (on && !full) btn.textContent = d.proveMore ? d.proveMore(cnt) : btn.textContent;
        else if (full) btn.textContent = d.proveFull || btn.textContent;
      } else {
        // X / Telegram 은 1회성.
        //  · 인장 미획득 → 인장 받기 버튼 (기존 동작)
        //  · 인장 획득 + 보너스 미수령 → "10,000 MOL 받기" 로 전환
        //  · 보너스 수령 완료 → 비활성
        const st2 = s as 'x' | 'telegram';
        if (on && !bonusClaimed(st2)) {
          // ★ 기존 addEventListener(X OAuth / 텔레그램 딥링크)를 떼어내야 한다.
          //   onclick 만 덮어써도 addEventListener 는 별개 슬롯이라 둘 다 실행되어,
          //   보너스를 청구하려 눌렀는데 X 승인창이 다시 뜨는 문제가 생긴다.
          //   노드를 복제해 교체하면 이전 리스너가 전부 사라진다.
          //   (인장을 이미 받은 상태이므로 원래 핸들러는 더 이상 필요 없다)
          const fresh = btn.cloneNode(true) as HTMLButtonElement;
          btn.parentNode?.replaceChild(fresh, btn);
          fresh.disabled = false;
          fresh.classList.add('can-add');
          fresh.textContent = d.bonusClaim;
          fresh.onclick = () => void claimBonus(st2, fresh);
        } else if (on) {
          btn.disabled = true;
          btn.classList.remove('can-add');
          btn.textContent = d.bonusReceived;
          btn.onclick = null;
        } else {
          btn.disabled = false;
          btn.classList.remove('can-add');
          btn.onclick = null; // 기존 addEventListener 핸들러가 동작
        }
      }
    }
  });
  const sc = document.getElementById('sealCount');
  if (sc) sc.textContent = d.sealCount(count);
  const open = count === 3;
  const dig = document.getElementById('dig');
  if (dig) dig.classList.toggle('dig-locked', !open);
  const mound = document.getElementById('mound');
  if (mound) mound.textContent = open ? '⛏️' : '⛰️';
  const dt = document.getElementById('digTitle');
  if (dt) dt.textContent = open ? d.digTitleOpen : d.digTitleLocked;
  const ds = document.getElementById('digSub');
  if (ds) ds.textContent = open ? d.digSubOpen : d.digSubLocked;
  const dbtn = document.getElementById('digBtn') as HTMLButtonElement | null;
  if (dbtn) {
    dbtn.textContent = open ? d.digBtnOpen : d.digBtnLocked;
    dbtn.disabled = !open;
  }
}
function refreshSealsFromServer(): void {
  if (!account) return;
  getJSON('/seals?wallet=' + account)
    .then((r) => {
      if (r && r.seals) {
        seals = r.seals;
        renderSeals();
      }
    })
    .catch(() => {});
  refreshBonusStatus();
}

// ---------- 인장 보너스 ----------
//  X / 텔레그램 인장에 각각 10,000 MOL.
//  온체인에서는 입금 claim 과 같은 claim() 을 타므로
//  MAX_CLAIMS_PER_WALLET(10) 카운터를 공유한다.
type BonusRow = { status?: string; onchain_tx?: string } | null;
type OnchainBonus = { x: boolean; telegram: boolean } | null;
let bonusState: { x: BonusRow; telegram: BonusRow; onchain: OnchainBonus } =
  { x: null, telegram: null, onchain: null };

function refreshBonusStatus(): void {
  if (!account) return;
  getJSON('/bonus/status?wallet=' + account)
    .then((r) => {
      if (r && typeof r === 'object') {
        bonusState = { x: r.x ?? null, telegram: r.telegram ?? null, onchain: r.onchain ?? null };
        renderSeals();
      }
    })
    .catch(() => {});
}

/**
 * 이 인장의 보너스를 이미 받았는지.
 *  ★ 온체인이 최종 진실이다.
 *   DB 가 pending 이어도 온체인이 미지급이면 아직 안 받은 것이다.
 *   (서명만 받고 지갑에서 거부한 경우 — 다시 받을 수 있어야 한다)
 *   온체인을 못 읽었을 때만 DB 로 판단한다.
 */
function bonusClaimed(sealType: 'x' | 'telegram'): boolean {
  if (bonusState.onchain) return bonusState.onchain[sealType];
  const b = bonusState[sealType];
  return !!b && b.status === 'confirmed';
}

/**
 * 인장 보너스 청구.
 *  · 백엔드가 가상 txid 로 서명을 발급하고, 사용자가 claim() 을 호출한다.
 *  · 온체인 usedTxid 가 지갑+인장당 1회를 보장한다.
 */
async function claimBonus(sealType: 'x' | 'telegram', btn: HTMLButtonElement): Promise<void> {
  if (!account) { alert(t().needConnect); return; }
  const d = t();
  const orig = btn.textContent;
  btn.disabled = true;
  btn.textContent = d.bonusClaiming;

  const restore = () => { btn.disabled = false; btn.textContent = orig; };

  // catch 에서 bonusId 를 참조해야 하므로 try 밖에 둔다.
  let sig: any = null;

  try {
    // 체인 확인 — 다른 네트워크면 provider 생성이 깨진다
    try {
      await wallet.ensureChain();
    } catch {
      alert(d.wrongChainAlert);
      restore();
      return;
    }

    sig = await post('/bonus/sign', { wallet: account, sealType });
    if (!sig || sig.error) {
      const code = sig && sig.error;
      if (code === 'already_claimed') alert(d.bonusAlready);
      else if (code === 'seal_not_earned') alert(d.bonusNeedSeal);
      else alert(d.errNothing);
      restore();
      return;
    }

    // ★ 인장 보상은 전용 컨트랙트(MOLSealClaim)를 쓴다.
    //   입금 보상(MOLClaim)과 분리되어 있어 10건 상한을 잠식하지 않는다.
    //   컨트랙트 주소는 서버 응답으로 받아 프론트 상수와 어긋날 여지를 없앤다.
    const provider: BrowserProvider = await wallet.getProvider();
    const signer = await provider.getSigner();
    const c = new Contract(sig.contract, SEAL_CLAIM_ABI, signer);
    const tx = await c.claimSeal(sig.sealTypeId, sig.deadline, sig.v, sig.r, sig.s);
    btn.textContent = d.bonusPending;
    const rc = await tx.wait();

    await post('/bonus/confirm', { bonusId: sig.bonusId, onchainTx: rc.hash });

    // ★ MOL 토큰을 지갑에 추가 제안 (입금 claim 과 동일).
    //   토큰이 목록에 없으면 받아도 잔액이 안 보여 "지급이 안 됐나" 오해한다.
    //   거부하거나 미지원이어도 지급은 이미 끝났으므로 조용히 넘어간다.
    await offerAddMolToken(provider);

    alert(d.bonusDone);
    refreshBonusStatus();
    refreshSealsFromServer();
  } catch (e: any) {
    // 사용자가 지갑에서 거부했거나 온체인 revert
    const msg = String(e?.message || e);
    dlog('보너스 claim 실패: ' + msg);

    // ★ 선점 해제. 이게 없으면 status=pending 이 남아
    //   같은 사용자가 재시도할 때 "이미 수령" 으로 막힌다.
    //   (서버도 온체인 기준으로 stale pending 을 정리하지만,
    //    즉시 풀어주는 편이 사용자 경험이 낫다)
    if (sig && sig.bonusId) {
      void post('/bonus/fail', { bonusId: sig.bonusId, reason: msg.slice(0, 200) });
    }

    if (!/reject|denied|cancel|4001/i.test(msg)) {
      alert(d.errNothing);
    }
    restore();
  }
}

// ---------- 지갑 (SDK) ----------
// 연결 진행 중 화면 중앙 오버레이 (모바일에서 "눌러도 반응 없음" 느낌 방지).
function showConnecting(msg: string): void {
  let ov = document.getElementById('connOverlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'connOverlay';
    ov.style.cssText =
      'position:fixed;inset:0;z-index:9500;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(6,8,13,.82);backdrop-filter:blur(2px)';
    ov.innerHTML =
      '<div style="background:var(--ink-2,#12151d);border:1px solid var(--gb,#D4A843);border-radius:14px;' +
      'padding:26px 30px;text-align:center;max-width:78vw">' +
      '<div class="conn-spin" style="width:34px;height:34px;margin:0 auto 14px;border:3px solid #333;' +
      'border-top-color:var(--gold,#D4A843);border-radius:50%;animation:connspin 1s linear infinite"></div>' +
      '<div id="connMsg" style="color:#eee;font-size:14px;line-height:1.5"></div>' +
      '<button id="connCancel" style="margin-top:16px;font-size:12px;padding:6px 16px;border-radius:7px;' +
      'border:1px solid var(--line-2,#3a4155);background:transparent;color:var(--txt-3,#888)">' + t().cancel + '</button>' +
      '</div>';
    document.body.appendChild(ov);
    // 스핀 애니메이션
    if (!document.getElementById('connspinStyle')) {
      const st = document.createElement('style');
      st.id = 'connspinStyle';
      st.textContent = '@keyframes connspin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }
    document.getElementById('connCancel')!.onclick = hideConnecting;
  }
  ov.style.display = 'flex';
  const m = document.getElementById('connMsg');
  if (m) m.textContent = msg;
}
function hideConnecting(): void {
  const ov = document.getElementById('connOverlay');
  if (ov) ov.style.display = 'none';
}

// 모바일 판별 — 딥링크 왕복 안내/텔레그램 스킴 분기에 공통 사용.
function isMobileUA(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent ?? '');
}

async function doConnect(): Promise<void> {
  dlog('doConnect() — 버튼 눌림');
  showConnecting(t().connecting);

  // 모바일: MetaMask 앱으로 딥링크 전환된 뒤에는 "승인하고 브라우저로 돌아오라"는
  //  안내로 바꿔준다. (SDK 의 원래 promise 는 복귀 시 유실될 수 있고,
  //   실제 연결 완성은 metamask.ts 의 eth_accounts 폴링이 담당한다.)
  let hintTimer: number | null = null;
  if (isMobileUA()) {
    hintTimer = window.setTimeout(() => {
      showConnecting(
        'MetaMask에서 연결을 승인한 뒤,\n이 브라우저로 돌아와 주세요.',
      );
    }, 2500);
  }

  try {
    const addr = await wallet.connect();
    dlog('wallet.connect() 반환: ' + addr);
    account = addr;

    // ★ 체인 전환 실패를 조용히 넘기지 않는다.
    //   여기서 무시하면 claim 시점에 provider 생성이 깨지며
    //   "Cannot read properties of undefined (reading 'id')" 라는
    //   원인 불명 메시지로 나타난다. (wagmi 의 client.chain 이 undefined)
    try {
      await wallet.ensureChain();
    } catch (ce: any) {
      dlog('ensureChain 실패: code=' + (ce && ce.code));
      alert(t().wrongChainAlert);
    }
    hideConnecting();
    reflectConnected();
  } catch (e: any) {
    dlog('doConnect catch: code=' + (e && e.code) + ' msg=' + (e && e.message));
    hideConnecting();
    // 인앱에서 연결이 막히면 외부 브라우저 안내
    if (isInAppBrowser()) {
      const ok = escapeToExternalBrowser();
      if (ok === false) alert(manualEscapeHint());
      return;
    }
    if (e && e.code === 'TIMEOUT') alert(t().connTimeout);
    else if (e && e.code === 'USER_REJECTED') { /* 사용자 취소 — 조용히 */ }
    else if (e && e.code === 'NO_WALLET') alert(t().errNoMM);
    else alert(t().cancelled);
  } finally {
    if (hintTimer !== null) clearTimeout(hintTimer);
  }
}

// 연결 해제 — 지갑을 끊고 UI를 초기(미연결) 상태로 되돌림.
//  · 주소를 잘못 연결했을 때 다시 연결하기 위함.
async function doDisconnect(): Promise<void> {
  try { await wallet.disconnect(); } catch { /* noop */ }
  account = null;
  validTxids = [];
  // UI 초기화
  $('dot').classList.remove('on');
  const wl = document.getElementById('walletLabel');
  if (wl) wl.style.display = '';
  const ad = document.getElementById('addr');
  if (ad) { ad.style.display = 'none'; ad.textContent = ''; ad.onclick = null; }
  const wbtns = document.getElementById('wbtns');
  if (wbtns) {
    // 원래 MetaMask 연결 버튼 복원
    wbtns.innerHTML = '<button class="wbtn mm" id="connectMM">MetaMask</button>';
    const mm = document.getElementById('connectMM');
    if (mm) mm.onclick = doConnect;
  }
  // 인장/목록 초기화
  const my = document.getElementById('myTxids');
  if (my) { my.style.display = 'none'; my.innerHTML = ''; }
}
function reflectConnected(): void {
  if (!account) return;
  $('dot').classList.add('on');
  const wl = document.getElementById('walletLabel');
  if (wl) wl.style.display = 'none';
  const ad = document.getElementById('addr');
  if (ad) {
    ad.style.display = 'inline-block';
    ad.textContent = short(account) + ' ⧉'; // 복사 아이콘 표시
    ad.style.cursor = 'pointer';
    ad.title = t().copyAddr;
    ad.onclick = () => copyToClipboard(account!, ad);
  }
  const wbtns = document.getElementById('wbtns');
  if (wbtns) {
    wbtns.innerHTML = '';
    const bound = document.createElement('span');
    bound.style.cssText = 'font-size:12px;color:var(--gold);font-weight:600;margin-right:10px';
    bound.textContent = '✓ bound';
    const disc = document.createElement('button');
    disc.className = 'wbtn-disc';
    disc.textContent = t().disconnect;
    disc.title = t().disconnect;
    disc.onclick = doDisconnect;
    wbtns.appendChild(bound);
    wbtns.appendChild(disc);
  }
  refreshSealsFromServer();
  void refreshMyTxids();
}

// 이 지갑이 이미 등록한 txid 목록을 불러와 입력 화면에 표시.
async function refreshMyTxids(): Promise<void> {
  const box = document.getElementById('myTxids');
  if (!box || !account) return;
  try {
    const r = await fetch(`${API_BASE}/wallet/txids?wallet=${account}`);
    const data = await r.json();
    renderMyTxids((data && data.items) || []);
  } catch {
    box.style.display = 'none';
  }
}

function renderMyTxids(items: any[]): void {
  const box = document.getElementById('myTxids');
  if (!box) return;
  const d = t();
  if (!items.length) {
    box.style.display = 'none';
    box.innerHTML = '';
    return;
  }
  const rows = items
    .map((it) => {
      const bnb = (() => {
        try { return (Number(BigInt(it.bnbValue)) / 1e18).toLocaleString(undefined, { maximumFractionDigits: 4 }); }
        catch { return ''; }
      })();
      const stLabel = it.status === 'confirmed' ? d.myTxConfirmed : d.myTxPending;
      const stCls = it.status === 'confirmed' ? 'mt-confirmed' : 'mt-pending';
      const shortTx = it.txid.slice(0, 10) + '…' + it.txid.slice(-8);
      return (
        `<div class="mt-row">` +
        `<span class="mt-tx" title="${it.txid}">${shortTx}</span>` +
        (bnb ? `<span class="mt-bnb">${bnb} BNB</span>` : '') +
        `<span class="mt-st ${stCls}">${stLabel}</span>` +
        `</div>`
      );
    })
    .join('');
  box.innerHTML =
    `<div class="mt-title">${d.myTxTitle} (${items.length})</div>` +
    `<div class="mt-list">${rows}</div>`;
  box.style.display = 'block';
}

// 클립보드 복사
function copyToClipboard(textVal: string, el: HTMLElement): void {
  const done = () => {
    const prev = el.textContent;
    el.textContent = t().copied;
    setTimeout(() => {
      el.textContent = prev;
    }, 1100);
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(textVal).then(done).catch(() => {
      fallbackCopy(textVal);
      done();
    });
  } else {
    fallbackCopy(textVal);
    done();
  }
}
function fallbackCopy(textVal: string): void {
  const ta = document.createElement('textarea');
  ta.value = textVal;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } catch {
    /* noop */
  }
  document.body.removeChild(ta);
}

// ---------- 인장: deposit 모달 ----------
function bindStaticHandlers(): void {
  const cMM = document.getElementById('connectMM');
  if (cMM) cMM.addEventListener('click', () => void doConnect());

  $('btnDeposit').addEventListener('click', () => {
    if (!account) {
      alert(t().needConnect);
      return;
    }
    $('verifyModal').classList.add('on');
  });
  $('modalClose').addEventListener('click', () => $('verifyModal').classList.remove('on'));

  // X 인장
  $('btnX').addEventListener('click', () => {
    if (!account) {
      alert(t().needConnect);
      return;
    }
    getJSON('/x/auth?wallet=' + account)
      .then((r) => {
        if (r.url) window.location.href = r.url;
      })
      .catch(() => {});
  });

  // 텔레그램 인장 (딥링크)
  $('btnTg').addEventListener('click', () => {
    if (!account) {
      alert(t().needConnect);
      return;
    }
    const startParam = 'w' + account.replace(/^0x/i, '').toLowerCase();

    // ★ ?start= 파라미터 유실 대응.
    //  · https://t.me/BOT?start=... 는 텔레그램 앱에 해당 봇 대화방이 이미 열려있으면
    //    파라미터를 무시하고 기존 대화방만 띄우는 경우가 있다 → 봇에는 맨 "/start" 만 도착.
    //  · tg://resolve?domain=..&start=.. 스킴은 파라미터를 더 안정적으로 전달한다.
    //  · 앱 미설치/스킴 실패 시에만 https 로 폴백.
    const tgScheme = `tg://resolve?domain=${TG_BOT_USERNAME}&start=${startParam}`;
    const httpsFallback = `https://t.me/${TG_BOT_USERNAME}?start=${startParam}`;

    if (isMobileUA()) {
      const t0 = Date.now();
      location.href = tgScheme;
      // 앱으로 전환됐다면 탭이 백그라운드가 되어 이 타이머는 늦게 돌거나 안 돈다.
      // 여전히 foreground 이고 시간도 거의 안 지났다면 = 스킴 실패 → https 폴백.
      setTimeout(() => {
        if (document.visibilityState === 'visible' && Date.now() - t0 < 2000) {
          location.href = httpsFallback;
        }
      }, 900);
    } else {
      window.open(httpsFallback, '_blank');
    }

    let tries = 0;
    const iv = setInterval(() => {
      tries++;
      refreshSealsFromServer();
      if (seals.telegram || tries > 60) clearInterval(iv);
    }, 5000);
  });

  // 검증 모드 전환
  $('modeScan').addEventListener('click', () => switchMode('scan'));
  $('modeTx').addEventListener('click', () => switchMode('txids'));
  $('txInput').addEventListener('input', refreshTxCounter);

  // 스캔 검증
  $('scanBtn').addEventListener('click', () => {
    if (!account) return;
    const b = $('scanBtn') as HTMLButtonElement;
    const o = b.textContent;
    b.disabled = true;
    b.textContent = '…';
    post('/verify/scan', { wallet: account, locale: lang })
      .then((data) => {
        renderResults(
          (data.validTxids || []).map((x: string) => ({ txid: x, ok: true })),
          data.validTxids || [],
        );
      })
      .catch(() => {})
      .then(() => {
        b.disabled = false;
        b.textContent = o;
      });
  });

  // txid 수동 검증
  $('verifyTxBtn').addEventListener('click', () => {
    const txids = parseTxids();
    if (!txids.length) return;
    if (txids.length > MAX_TXIDS) {
      mToast(t().overLimit, 'err');
      return;
    }
    const b = $('verifyTxBtn') as HTMLButtonElement;
    const o = b.textContent;
    b.disabled = true;
    b.textContent = '…';
    post('/verify/txids', { wallet: account, txids, locale: lang })
      .then((data) => renderResults(data.results || [], data.validTxids || []))
      .catch(() => {})
      .then(() => {
        b.textContent = o;
        refreshTxCounter();
      });
  });

  // claim
  $('claimBtn').addEventListener('click', () => void doClaim());

  // 체인 경고 배너의 전환 버튼
  const bsc = document.getElementById('btnSwitchChain');
  if (bsc) {
    bsc.addEventListener('click', () => {
      void wallet.ensureChain().catch((e: unknown) => dlog('수동 전환 실패: ' + String(e)));
    });
  }

  // dig
  $('digBtn').addEventListener('click', () => {
    if (!seals.deposit) {
      $('verifyModal').classList.add('on');
      return;
    }
    burst();
  });

  // dex 배너 닫기
  const dexClose = document.getElementById('dexClose');
  if (dexClose)
    dexClose.addEventListener('click', () => {
      const db = document.getElementById('dexban');
      if (db) db.style.display = 'none';
      document.body.style.paddingBottom = '0';
    });

  // 언어 전환
  const langEl = document.getElementById('lang');
  if (langEl)
    langEl.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('button[data-lang]');
      if (!b) return;
      lang = b.getAttribute('data-lang')!;
      applyI18n();
    });
}

function switchMode(mode: string): void {
  $('modeScan').classList.toggle('active', mode === 'scan');
  $('modeTx').classList.toggle('active', mode === 'txids');
  $('scanBox').style.display = mode === 'scan' ? 'block' : 'none';
  $('txBox').style.display = mode === 'txids' ? 'block' : 'none';
  $('results').innerHTML = '';
  $('modalSummary').style.display = 'none';
  $('claimBtn').style.display = 'none';
  validTxids = [];
}
function parseTxids(): string[] {
  return ($('txInput') as HTMLTextAreaElement).value
    .split(/\s+/)
    .map((s) => s.trim())
    .filter((s) => s.indexOf('0x') === 0);
}
function refreshTxCounter(): void {
  const n = parseTxids().length;
  const c = document.getElementById('txCounter');
  if (!c) return;
  c.textContent = n + ' / ' + MAX_TXIDS;
  c.classList.toggle('over', n > MAX_TXIDS);
  ($('verifyTxBtn') as HTMLButtonElement).disabled = n === 0 || n > MAX_TXIDS;
}
/**
 * 검증 실패 사유를 사용자 언어로 바꾼다.
 *  사유를 안 보여주면 "유효한 입금이 없습니다" 만 뜨고 끝나서,
 *  금액이 모자란 건지 거래소가 아닌 건지 알 수 없다.
 *  문의로 이어지는 가장 흔한 지점이다.
 */
function reasonText(reason: string): string {
  const d = t();
  switch (reason) {
    case 'below_min_amount':   return d.rsnBelowMin;
    case 'sender_not_exchange': return d.rsnNotExchange;
    case 'recipient_mismatch':  return d.rsnRecipient;
    case 'tx_not_found':        return d.rsnNotFound;
    case 'tx_not_confirmed':    return d.rsnNotConfirmed;
    default:                    return d.rsnUnknown;
  }
}

function renderResults(results: any[], valid: string[]): void {
  const box = $('results');
  box.innerHTML = '';
  const dd = t();
  (results || []).forEach((r) => {
    const row = document.createElement('div');
    // 3가지 상태: 이미사용됨(used) / 통과(ok) / 실패(no)
    if (r.ok && r.alreadyUsed) {
      row.className = 'res-row used';
      row.innerHTML =
        `<span class="res-tx">${r.txid || ''}</span>` +
        `<span class="res-used">${dd.alreadyClaimed}</span>`;
    } else {
      row.className = 'res-row ' + (r.ok ? 'ok' : 'no');
      // 실패한 건은 사유를 함께 보여준다.
      const why = r.ok ? '' : `<span class="res-why">${reasonText(r.reason)}</span>`;
      row.innerHTML =
        `<span class="res-tx">${r.txid || ''}</span>` +
        why +
        `<span>${r.ok ? '✓' : '✕'}</span>`;
    }
    box.appendChild(row);
  });
  validTxids = (valid || []).slice(0, MAX_TXIDS);
  const count = validTxids.length;
  const d = t();
  $('modalSummary').style.display = 'flex';
  if (count > 0) {
    // 유효 입금들의 총 BNB 합산 (wei → BNB). value 는 백엔드가 문자열(wei)로 반환.
    let totalWei = 0n;
    (results || []).forEach((r) => {
      if (r.ok && r.value) {
        try { totalWei += BigInt(r.value); } catch { /* 무시 */ }
      }
    });
    const totalBnb = Number(totalWei) / 1e18;
    // 보기 좋게: 소수 4자리까지, 뒤 0 제거
    const bnbStr = totalBnb.toLocaleString(undefined, { maximumFractionDigits: 4 });
    // 큰 글씨: "1 deposit (1.946 BNB) ✓" — 건수 + 실제 금액
    $('sumMol').textContent = d.depositsFound(count, bnbStr);
    // 부가: 그래서 받을 MOL
    $('sumDetail').textContent = d.willGetMol((count * 10000).toLocaleString());
    const cb = $('claimBtn') as HTMLButtonElement;
    cb.style.display = 'block';
    cb.disabled = false;
    cb.textContent = d.claimAmt((count * 10000).toLocaleString());
  } else {
    // 0건인데 이미 사용된 txid 가 있으면 → "입금 없음" 이 아니라 "이미 청구됨"
    const anyUsed = (results || []).some((r) => r.ok && r.alreadyUsed);
    if (anyUsed) {
      $('sumMol').textContent = d.alreadyClaimedBig;
      $('sumDetail').textContent = d.alreadyClaimedNote;
      $('claimBtn').style.display = 'none';
      return;
    }
    // 0건: MOL 이 아니라 "BNB 입금 없음" 으로 표시 (MOL 이동 확인으로 착각 방지)
    $('sumMol').textContent = d.noDepositsBig;
    // 실패 사유가 하나로 모이면 그걸 보여준다. 여러 개면 일반 메시지.
    const reasons = new Set(
      (results || []).filter((r) => !r.ok && r.reason).map((r) => r.reason as string),
    );
    $('sumDetail').textContent =
      reasons.size === 1 ? reasonText([...reasons][0]) : d.noValid;
    $('claimBtn').style.display = 'none';
    // 스캔 모드에서 0건이면 → 직접 TXID 입력 유도 (핫월렛 목록에 없을 수 있으니)
    const scanActive = $('scanBox').style.display !== 'none';
    if (scanActive) {
      const box = $('results');
      const tip = document.createElement('div');
      tip.className = 'res-tip';
      tip.style.cssText =
        'margin-top:12px;padding:12px;border:1px solid var(--gold);border-radius:8px;background:rgba(212,168,67,.08);font-size:12.5px;line-height:1.6';
      tip.innerHTML =
        '<div style="color:var(--gold);font-weight:700;margin-bottom:6px">' +
        d.tryTxidTitle +
        '</div><div style="color:var(--txt-2)">' +
        d.tryTxidBody +
        '</div>';
      const btn = document.createElement('button');
      btn.className = 'ghost-btn';
      btn.style.cssText = 'margin-top:10px';
      btn.textContent = d.tryTxidBtn;
      btn.addEventListener('click', () => {
        switchMode('txids');
        const g = document.getElementById('txGuide');
        if (g) (g as HTMLDetailsElement).open = true;
      });
      tip.appendChild(btn);
      box.appendChild(tip);
    }
  }
}
function mToast(m: string, k: string): void {
  const el = $('modalToast');
  el.textContent = m;
  el.className = 'toast show ' + k;
}

// ---------- MOL 토큰 지갑 추가 제안 ----------
// claim 성공 후, 지갑에 MOL 잔액이 잡히지 않으면(=토큰 미등록) watchAsset 으로 추가 제안.
//  · 이미 잔액이 보이면 굳이 팝업 안 띄움.
async function offerAddMolToken(provider: BrowserProvider): Promise<void> {
  if (!MOL_TOKEN || !account) return;
  try {
    // wallet_watchAsset 은 이미 등록된 토큰이면 MetaMask 가 알아서 스킵/무시함.
    //  · 잔액(balanceOf)으로 판단하면 안 됨: 토큰이 지갑에 "표시" 안 돼도
    //    온체인 잔액은 있으므로(특히 2번째 claim 부터) 팝업이 안 뜨는 버그.
    //  · 따라서 잔액 체크 없이 항상 추가 제안 → 미등록이면 팝업, 등록됐으면 무시.
    const eth = (window as any).ethereum;
    if (!eth || !eth.request) return;
    await eth.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: MOL_TOKEN,
          symbol: MOL_SYMBOL,
          decimals: MOL_DECIMALS,
        },
      },
    });
  } catch {
    // 사용자가 거부하거나 지원 안 하면 조용히 무시 (지급은 이미 완료됨)
  }
}

// ---------- claim (SDK provider 사용) ----------
async function doClaim(): Promise<void> {
  if (!account || !validTxids.length) return;
  const cb = $('claimBtn') as HTMLButtonElement;
  cb.disabled = true;
  cb.textContent = '…';
  const d = t();
  let claimId: string | null = null;
  const resetBtn = () => {
    cb.disabled = false;
    cb.textContent = d.claimAmt((validTxids.length * 10000).toLocaleString());
  };
  try {
    // ★ claim 직전 체인 재확인 (마지막 방어선).
    //   연결 후 사용자가 MetaMask 에서 네트워크를 바꿨을 수 있다.
    //   틀린 체인이면 wagmi 의 client.chain 이 undefined 가 되어
    //   getProvider() 안에서 "reading 'id'" 로 죽는다.
    //   ensureChain() 이 switchChain 을 호출하므로 지갑에 전환 팝업이 뜬다.
    try {
      await wallet.ensureChain();
    } catch (ce: any) {
      mToast(
        ce && ce.code === 'USER_REJECTED' ? d.wrongChainRejected : d.wrongChainAlert,
        'err',
      );
      resetBtn();
      return;
    }

    const provider: BrowserProvider = await wallet.getProvider();
    // 1) 가스비 확인
    const bal = await provider.getBalance(account);
    if (bal < parseEther('0.0005')) {
      mToast(d.noGas, 'err');
      resetBtn();
      return;
    }
    const signer = await provider.getSigner();
    const contract = new Contract(CLAIM_CONTRACT, CLAIM_ABI, signer);
    let nonce = 0;
    try {
      nonce = Number(await contract.nonces(account));
    } catch {
      nonce = 0;
    }
    // 2) 서명 요청
    const sig = await post('/sign', { wallet: account, txids: validTxids, nonce, locale: lang });
    if (sig.error) {
      const m =
        sig.error === 'claimer_limit_reached'
          ? d.errClosed
          : sig.error === 'wallet_cap_exceeded'
            ? d.errCap
            : 'Error: ' + sig.error;
      mToast(m, 'err');
      resetBtn();
      return;
    }
    claimId = sig.claimId;
    // 3) 가스 추정 (실패해도 진행)
    await contract.claim.estimateGas(sig.txids, sig.deadline, sig.v, sig.r, sig.s).catch(() => null);
    // 4) 전송
    const tx = await contract.claim(sig.txids, sig.deadline, sig.v, sig.r, sig.s);
    const rc = await tx.wait();
    await post('/claim/confirm', { claimId, onchainTx: (rc && rc.hash) || tx.hash });
    mToast(d.dug((validTxids.length * 10000).toLocaleString()), 'ok');
    seals.deposit = true;
    renderSeals();
    // claim 성공 → 지갑에 MOL 토큰이 안 보이면 추가 제안 (watchAsset)
    void offerAddMolToken(provider);
    void refreshMyTxids();
    setTimeout(() => {
      $('verifyModal').classList.remove('on');
      burst();
    }, 1200);
  } catch (e: any) {
    let msg = (e && e.message) || 'error';
    // 컨트랙트 커스텀 에러 셀렉터 → 사람이 읽을 메시지.
    //  · revert data 는 e.data 또는 e.info.error.data 또는 message 안에 들어옴.
    const rawData: string =
      (e && (e.data || (e.info && e.info.error && e.info.error.data))) ||
      (typeof msg === 'string' ? (msg.match(/0x[0-9a-fA-F]{8,}/)?.[0] ?? '') : '');
    const selector = (rawData || '').slice(0, 10).toLowerCase();
    const ERR: Record<string, string> = {
      '0xc8250fb4': d.errTxidUsed,      // TxidAlreadyUsed
      '0x61330e93': d.errBadSigner,     // BadSigner
      '0x203d82d8': d.errExpired,       // Expired
      '0x83ebf0b2': d.errWalletCap,     // WalletCapExceeded
      '0x5e07a741': d.errClaimerLimit,  // ClaimerLimitReached
      '0x1309a563': d.errPaused,        // IsPaused
      '0x969bf728': d.errNothing,       // NothingToClaim
      '0x90b8ec18': d.errTransferFail,  // TransferFailed
    };
    if (ERR[selector]) msg = ERR[selector];
    else if (/insufficient funds|insufficient balance|gas required/i.test(msg)) msg = d.noGas;
    else if (/user rejected|user denied|action_rejected/i.test(msg)) msg = d.cancelled;
    if (claimId) post('/claim/fail', { claimId, reason: (e && e.message) || 'onchain_failed' });
    mToast(msg, 'err');
    resetBtn();
  }
}

function burst(): void {
  const host = $('dig');
  const icons = ['🪙', '✨', '⛏️', '🟡'];
  for (let i = 0; i < 14; i++) {
    ((i) => {
      setTimeout(() => {
        const n = document.createElement('span');
        n.className = 'nugget';
        n.textContent = icons[i % icons.length];
        n.style.left = 35 + Math.random() * 30 + '%';
        n.style.top = '40%';
        host.appendChild(n);
        setTimeout(() => n.remove(), 1000);
      }, i * 60);
    })(i);
  }
}

function loadEventStatus(): void {
  getJSON('/event/status')
    .then((s) => {
      const bar = document.getElementById('fcfsBar');
      if (bar) bar.style.display = 'block';
      const pct = Math.min(100, (s.claimedWallets / s.maxClaimers) * 100);
      const fill = document.getElementById('fcfsFill');
      if (fill) fill.style.width = pct + '%';
      const rem = s.closed ? t().fcfsClosed : t().fcfsRemaining(s.remaining.toLocaleString());
      const ft = document.getElementById('fcfsText');
      if (ft)
        ft.textContent =
          s.claimedWallets.toLocaleString() + ' / ' + s.maxClaimers.toLocaleString() + ' · ' + rem;
      if (s.closed) {
        (document.getElementById('connectMM') as HTMLButtonElement | null)?.setAttribute('disabled', 'true');
      }
    })
    .catch(() => {});
}

// ---------- 후원 주소 ----------
//  EOA(개인 지갑)이라 Ethereum / BNB Chain / Polygon 이 같은 주소를 공유한다.
//  주소는 config 에서 주입 → 나중에 멀티시그로 옮길 때 .env 만 바꾸면 된다.
function setupDonate(): void {
  const addrEl = document.getElementById('donateAddr');
  const copyEl = document.getElementById('donateCopy');
  if (addrEl) addrEl.textContent = DONATE_ADDRESS;
  if (!copyEl) return;

  copyEl.addEventListener('click', async () => {
    try {
      // navigator.clipboard 는 HTTPS(또는 localhost)에서만 동작한다.
      // HTTP 환경 대비로 execCommand 폴백을 둔다.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(DONATE_ADDRESS);
      } else {
        const ta = document.createElement('textarea');
        ta.value = DONATE_ADDRESS;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      copyEl.classList.add('done');
      setTimeout(() => copyEl.classList.remove('done'), 1200);
    } catch {
      /* 복사 실패는 조용히 무시 — 주소는 화면에 그대로 보인다 */
    }
  });
}
// ---------- boot ----------
/**
 * X OAuth 콜백 결과 처리.
 *  백엔드가 /?x=ok | err_dup | err_follow | ... 로 리다이렉트한다.
 *  이걸 읽지 않으면 실패해도 화면이 그대로여서, 사용자는
 *  "왜 인장이 안 뜨지" 만 반복하게 된다. (실제로 겪은 문제)
 *  처리 후 쿼리를 지워 새로고침 시 메시지가 반복되지 않게 한다.
 */
function handleXCallback(): void {
  let p: URLSearchParams;
  try { p = new URLSearchParams(location.search); } catch { return; }
  const x = p.get('x');
  if (!x) return;

  const d = t();
  if (x === 'ok') {
    const h = p.get('handle');
    dlog('X 인증 성공: @' + (h || ''));
    // 인장 반영은 refreshSealsFromServer 가 처리한다.
  } else if (x === 'err_dup') {
    alert(d.xErrDup);
  } else if (x === 'err_follow') {
    alert(d.xErrFollow);
  } else {
    // err_state / err_token / err_me / err_target / err
    alert(d.xErrGeneric);
  }

  // 쿼리 제거 (새로고침 시 재알림 방지)
  try {
    p.delete('x'); p.delete('handle'); p.delete('status');
    const qs = p.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
  } catch { /* noop */ }
}

function boot(): void {
  initDebug();
  dlog('boot() 시작');
  bindStaticHandlers();
  applyI18n();
  handleXCallback();
  setupDonate();
  setupInApp();
  loadEventStatus();
  // 기존 세션 복원 (WalletConnect/injected)
  wallet
    .restore()
    .then(() => {
      const a = wallet.getAddress();
      if (a) {
        account = a;
        reflectConnected();
      } else {
        // 딥링크 좀비 → 새로고침 복귀 시: mb_connecting 플래그가 있으면 자동 재연결.
        //  · 새로 로드된 페이지의 window.ethereum 은 살아있으므로 이번엔 연결됨.
        try {
          const flag = sessionStorage.getItem('mb_connecting');
          if (flag) {
            const age = Date.now() - Number(flag);
            if (age < 180000) {
              dlog('리로드 후 자동 재연결 시도 (mb_connecting)');
              showConnecting(t().connecting);
              void doConnect();
            } else {
              sessionStorage.removeItem('mb_connecting');
            }
          }
        } catch { /* noop */ }
      }
    })
    .catch(() => {});
  // ★ 앱(텔레그램/MetaMask) 다녀와서 복귀했을 때 인장 상태 재조회.
  //  · 모바일에서는 location.href 로 앱 전환하므로 btnTg 의 setInterval 폴링이
  //    페이지 이탈과 함께 죽는다 → 복귀 시점에 서버에서 다시 읽어와야 반영된다.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!account) return;
    refreshSealsFromServer();
    // 텔레그램 봇 처리(webhook→DB)가 살짝 늦을 수 있어 몇 초 뒤 한 번 더.
    setTimeout(() => { if (account) refreshSealsFromServer(); }, 2500);
  });

  // 지갑 상태 변화 반영
  wallet.onChange((snap) => {
    if (!snap.connected) {
      account = null;
    } else if (snap.address) {
      account = snap.address;
    }
    // ★ 사용자가 MetaMask 에서 직접 네트워크를 바꾼 경우를 잡아 배너로 알린다.
    //   claim 을 눌러야 알 수 있으면 이미 늦다.
    const warn = document.getElementById('chainWarn');
    if (warn) {
      warn.style.display = snap.connected && !snap.chainOk ? 'block' : 'none';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// links.js 의 window.MoleLinks 타입 (전역)
declare global {
  interface Window {
    MoleLinks?: any;
  }
}